import React from 'react';
import classNames from 'classnames';
import { useEnvironment } from '@wix/yoshi-flow-editor';
import { type LayoutName } from '@wix/communities-blog-client-common';
import { usePostTitleTag } from '../../context/post-title-tag-context';
import useIsFeedDesignEnabled from '../../hooks/use-is-feed-design-enabled';
import useLayoutColorClasses from '../../hooks/use-layout-color-classes';
import { getFeedColorClassName } from '../../services/layout-config';
import DotDotDot from '../dotdotdot';
import Link from '../link/internal-link';
import styles from './post-title.scss';

type PostTitleType = 'small' | 'medium' | 'large' | 'single' | 'search';

function isPostTitleType(type: string): type is PostTitleType {
  return ['small', 'medium', 'large', 'single', 'search'].includes(type);
}

type Props = {
  className?: string;
  fullRoute?: string | null;
  lineCount?: number;
  linkClassName?: string;
  showCategoryLabel?: boolean;
  style?: React.CSSProperties;
  title: React.ReactNode | undefined;
  to?: string;
  type: LayoutName | PostTitleType;
};

export const PostTitle: React.FC<Props> = ({
  style = {},
  className,
  lineCount,
  linkClassName,
  title,
  to,
  type,
  showCategoryLabel = false,
  fullRoute,
}) => {
  const { tagName: Component } = usePostTitleTag();
  const { isMobile } = useEnvironment();
  const { textColorClassName } = useLayoutColorClasses();
  const { applyFeedDesign, getPostClassName } = useIsFeedDesignEnabled();
  const titleClassName = classNames(
    styles.title,
    styles[type],
    className,
    !applyFeedDesign && textColorClassName,
    'post-title',
    'blog-hover-container-element-color',
    type === 'single' && styles.singleNew,
    getPostClassName(
      getFeedColorClassName(isPostTitleType(type) ? undefined : type, 'title-color'),
      'title-font',
    ),
    showCategoryLabel && styles.withCategoryLabel,
  );

  const fontClassName = !isMobile ? className : undefined;
  const ellipsisTitle = lineCount ? (
    <DotDotDot useExactLineHeight={true} clamp={lineCount} className={fontClassName}>
      {title}
    </DotDotDot>
  ) : (
    <span className={fontClassName}>{title}</span>
  );

  return (
    <Component className={titleClassName} style={style} data-hook="post-title">
      {to ? (
        <Link
          fullRoute={fullRoute}
          className={classNames('post-title__text', styles.link, linkClassName)}
          to={to}
        >
          {ellipsisTitle}
        </Link>
      ) : (
        <span className={classNames('post-title__text', fontClassName)}>{ellipsisTitle}</span>
      )}
    </Component>
  );
};

export default PostTitle;
